import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, ChevronsUpDown } from 'lucide-react'
import { showApiError, showWarning } from '@/lib/utils/notifications'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import configSortStyles from '@/components/listing/propertySortHeaderButton.module.css'
import { cn } from '@/lib/utils/helpers'
import {
  ListingDataTableCard,
  LISTING_DATA_TABLE_CLASS,
} from '@/components/ui/listingDataTable'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { getOrganizations } from '../services/organizationsService'
import type {
  Organization,
  OrganizationListParams,
  OrganizationSortKey,
  SortDirection,
} from '../types/organization'

const ORGLIST_SESSION_KEY = 'orglist_filter'

const DEFAULT_PARAMS: OrganizationListParams = {
  page: 1,
  perPage: 15,
  sortBy: 'asc',
  orderBy: 'org_name',
}

function loadOrgListSessionParams(): OrganizationListParams {
  try {
    const saved = sessionStorage.getItem(ORGLIST_SESSION_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as OrganizationListParams
      if (parsed && typeof parsed === 'object') {
        return {
          ...DEFAULT_PARAMS,
          ...parsed,
        }
      }
    }
  } catch {
    showWarning('Could not restore your saved organization filters.')
  }
  return DEFAULT_PARAMS
}

function saveOrgListSessionParams(params: OrganizationListParams) {
  try {
    sessionStorage.setItem(ORGLIST_SESSION_KEY, JSON.stringify(params))
  } catch {
    showWarning('Could not save your organization filter preferences.')
  }
}

function clearOrgListSessionParams() {
  try {
    sessionStorage.removeItem(ORGLIST_SESSION_KEY)
  } catch {
    showWarning('Could not clear your saved organization filters.')
  }
}

export function OrganizationListPage() {
  const [params, setParams] = useState<OrganizationListParams>(loadOrgListSessionParams)
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [pagination, setPagination] = useState({ current: 1, last: 1 })
  const [searchInput, setSearchInput] = useState(() => loadOrgListSessionParams().q ?? '')
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(false)

  const load = useCallback(async (next: OrganizationListParams) => {
    setLoading(true)
    try {
      saveOrgListSessionParams(next)
      const response = await getOrganizations(next)
      setOrgs(response.data ?? [])
      setPagination({ current: response.current ?? 1, last: response.last ?? 1 })
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(params)
  }, [load, params])

  const applyParams = useCallback(
    (next: OrganizationListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: OrganizationSortKey) => {
    const nextDir: SortDirection = params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir, page: 1 })
  }

  const submitFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applyParams({ ...params, q: searchInput.trim() || undefined, page: 1 })
  }

  const resetFilter = () => {
    clearOrgListSessionParams()
    setSearchInput('')
    applyParams(DEFAULT_PARAMS)
  }

  const goToPage = (pageNo: number) => applyParams({ ...params, page: pageNo })

  const SortHeader = ({ column, label, className }: { column: OrganizationSortKey; label: string; className?: string }) => {
    const isActive = params.orderBy === column
    return (
      <th
        className={cn("select-none cursor-pointer !text-left", className)}
        onClick={() => toggleSort(column)}
      >
        <button
          type="button"
          className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start')}
          onClick={(event) => {
            event.stopPropagation()
            toggleSort(column)
          }}
        >
          <span className={parityStyles.sortHeaderLabel}>{label}</span>
          <span
            aria-hidden="true"
            className={cn(parityStyles.sortIcon, isActive && parityStyles.sortIconActive)}
          >
            {isActive ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
          </span>
        </button>
      </th>
    )
  }

  const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const
  const changePerPage = (perPage: number) => {
    applyParams({ ...params, perPage, page: 1 })
  }
  const showNumericPagination = !loading && (pagination.last > 1 || orgs.length > 0)

  return (
    <PageShell
      title="ORGANIZATIONS"
      headerAction={
        <Link
          to="/organizations/add"
          className={cn(primaryEntityActionButtonClass, "w-full sm:w-auto")}
        >
          Add Organization
        </Link>
      }
    >
      <form onSubmit={submitFilter} className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search organizations..."
          className="w-full sm:w-auto min-w-[200px] flex-1 sm:flex-none px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="submit"
          className={cn(primaryEntityActionButtonClass, "px-4 py-2 text-sm font-medium")}
        >
          Go
        </button>
        <button
          type="button"
          onClick={resetFilter}
          className={cn(secondaryEntityActionButtonClass, "px-4 py-2 text-sm font-medium")}
        >
          Reset
        </button>
      </form>

      <ListingDataTableCard>
        {showNumericPagination && (
          <ListPaginationBar
            placement="header"
            currentPage={pagination.current}
            lastPage={pagination.last}
            totalRecords={0}
            perPage={params.perPage ?? 15}
            onPageChange={goToPage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix="organization"
          />
        )}

        <div className="w-full">
          <table className={cn(parityStyles.companyListingTable, parityStyles.listingTable, LISTING_DATA_TABLE_CLASS)}>
            <thead className="border-b border-[#c8d7e8] bg-[#dfeaf5] text-slate-900">
            <tr>
              <SortHeader column="org_name" label="Name" className="w-[30%] !text-left" />
              <SortHeader column="org_abbrev" label="Abbr" className="w-[15%] !text-left" />
              <SortHeader column="charity" label="Charity" className="w-[15%] !text-left" />
              <SortHeader column="org_website" label="Website" className="w-[30%] !text-left" />
              <th className="px-4 py-3 !text-center !whitespace-nowrap w-[10%]">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#dfe7f0] [&>tr:nth-child(even)]:bg-[#f2f7fd]">
            {loading ? (
              <ListingTableLoadingRow colSpan={5} />
            ) : orgs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No organizations found.
                </td>
              </tr>
            ) : (
              orgs.map((org) => (
                <tr key={org.org_id} className={parityStyles.listingTableRow}>
                  <td className="px-4 py-3 font-medium text-slate-900 break-words !text-left">{org.org_name}</td>
                  <td className="px-4 py-3 text-slate-700 !text-left">{org.org_abbrev}</td>
                  <td className="px-4 py-3 text-slate-700 !text-left">
                    {String(org.charity) === '1' ? 'Yes' : 'No'}
                  </td>
                  <td className="px-4 py-3 text-slate-600 break-words !text-left">
                    {org.org_website ? (
                      <a
                        href={org.org_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1597ff] hover:text-[#0d7acc]"
                      >
                        {org.org_website}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-2">
                      <Link
                        to={`/organizations/edit/${org.org_id}`}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {showNumericPagination && (
          <ListPaginationBar
            placement="footer"
            currentPage={pagination.current}
            lastPage={pagination.last}
            totalRecords={0}
            perPage={params.perPage ?? 15}
            onPageChange={goToPage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix="organization"
          />
        )}
      </ListingDataTableCard>

    </PageShell>
  )
}
